import { useUserStore } from '@stores/users'

const protectedRoute = (role) => {
  return {
    requiresAuth: true,
    requiredRole: role || undefined
  }
}

export default [
  {
    path: '/spa/v',
    component: () => import('@layouts/ApplicationDataWrapper.vue'),
    meta: protectedRoute('vendor'),
    beforeEnter: async() => {
      const userStore = useUserStore()
      if (!userStore.user?.id) {
        await userStore.fetchMe()
      }
      if (userStore.isPartner) {
        return { name: 'campaigns' }
      }
    },
    children: [
      {
        path: 'partners',
        meta: {
          title: 'Partners',
          breadcrumb: {
            label: 'Partners',
            icon: 'handshake',
          }
        },
        children: [
          {
            name: 'partners',
            path: '',
            component: () => import('@pages/partners.vue'),
            meta: { title: 'Partners' }
          },
          {
            name: 'edit-partner',
            path: 'edit/:partnerId',
            component: () => import('@pages/vendor/EditPartnerPage.vue'),
            props: route => ({ partnerId: Number(route.params.partnerId) }),
            meta: {
              title: 'Edit Partner',
              breadcrumb: {
                label: 'Edit Partner',
              },
            },
          },
          {
            name: 'new-partner-info-field',
            path: 'data-fields/new',
            component: () => import('@pages/vendor/NewField.vue'),
            meta: {
              title: 'New Partner Info Field',
              breadcrumb: {
                label: 'New Partner Info Field',
              },
            },
          },
          {
            name: 'edit-partner-info-field',
            path: 'data-fields/edit/:fieldId',
            component: () => import('@pages/vendor/EditField.vue'),
            props: route => ({ fieldId: route.params.fieldId }),
            meta: {
              title: 'Edit Partner Info Field',
              breadcrumb: {
                label: 'Edit Partner Info Field',
              },
            },
          },
          {
            name: 'new-partner-info-form',
            path: 'data-forms/new',
            component: () => import('@pages/vendor/NewPartnerInfoForm.vue'),
            meta: {
              title: 'New Partner Info Form',
              breadcrumb: {
                label: 'New Partner Info Form',
              },
            },
          },
          {
            name: 'edit-partner-info-form',
            path: 'data-forms/edit/:partnerInfoFormId',
            component: () => import('@pages/vendor/EditPartnerInfoForm.vue'),
            props: route => ({ partnerInfoFormId: Number(route.params.partnerInfoFormId) }),
            meta: {
              title: 'Edit Partner Info Form',
              breadcrumb: {
                label: 'Edit Partner Info Form',
              },
            },
          },
        ]
      },
      {
        name: 'vendor-settings',
        path: 'vendor-settings',
        component: () => import('@pages/vendor-settings.vue'),
        meta: { 
          title: 'Team Settings',
          breadcrumb: {
            label: 'Team Settings',
            icon: 'settings',
          }
        },
      },
      {
        name: 'campaign-template-builder',
        path: 'campaigns/:campaignId/campaign-builder',
        component: () => import('@pages/campaigns/campaign-template-builder.vue'),
        meta: { title: 'Campaign Template Builder' }
      },
      {
        path: 'assets',
        meta: { 
          title: 'Assets', 
          breadcrumb: {
            label: 'Assets',
            icon: 'folder_open',
          } 
        },
        children: [
          {
            name: 'assets',
            path: '',
            component: () => import('@pages/vendor/AssetsPage.vue'),
            meta: { title: 'Assets' }
          },
          {
            name: 'edit-asset',
            path: 'edit/:assetId',
            meta: { 
              title: 'Edit Asset',
              breadcrumb: {
                label: 'Edit Asset',
              }, 
            },
            component: () => import('@pages/vendor/EditAssetForm.vue'),
            props: route => ({ assetId: Number(route.params.assetId) }),
          },
        ]
      },
      {
        path: 'copybuilder',
        meta: { 
          title: 'Copybuilder',
          breadcrumb: {
            label: 'Copybuilder',
            icon: 'description',
          }
        },
        children: [
          {
            name: 'generated-campaigns-list', 
            path: '',
            component: () => import('@pages/ai-campaign-features/GeneratedCampaignsList.vue'),
            meta: { title: 'Generated Campaigns' }
          },
          {
            name: 'ai-campaign-generator',
            path: 'edit/:id',
            component: () => import('@pages/ai-campaign-features/AICampaignGenerator.vue'),
            props: route => ({ id: Number(route.params.id) }),
            meta: {
              title: 'Edit Campaign',
              breadcrumb: {
                label: 'Edit Campaign'
              }
            }
          },
        ]
      },
      {
        name: 'matcher',
        path: 'matcher',
        component: () => import('@pages/matcher.vue'),
        meta: {
          title: 'Matcher',
          breadcrumb: {
            label: 'Matcher',
            icon: 'search',
          }
        },
      },
      {
        path: 'portals',
        meta: { 
          breadcrumb: {
            label: 'Portals',
            icon: 'hub',
          },
        },
        children: [
          {
            name: 'portals',
            path: '',
            component: () => import('@pages/vendor/portals/VendorPortals.vue'),
            meta: {
              title: 'Portals',
            }
          },
          {
            name: 'new-portal',
            path: 'new',
            component: () => import('@pages/vendor/portals/NewPortalForm.vue'),
            meta: {
              title: 'New Portal', 
              breadcrumb: {
                label: 'New Portal',
              },
            },
          },
          {
            name: 'edit-portal',
            path: 'edit/:portalId',
            component: () => import('@pages/vendor/portals/EditPortalForm.vue'),
            props: route => ({ portalId: Number(route.params.portalId) }),
            meta: {
              title: 'Edit Portal', 
              breadcrumb: {
                label: 'Edit Portal',
              },
            },
          },
        ]
      },
      {
        path: 'deals',
        meta: {
          breadcrumb: {
            label: 'Deals',
            icon: 'valve',
            iconType: 'symbol',
          },
        },
        children: [
          {
            name: 'deals',
            path: '',
            component: () => import('@pages/vendor/VendorDealsPage.vue'),
            meta: {
              title: 'Deals',
            }
          },
          {
            name: 'new-deal-reg-form',
            path: 'deal-reg-forms/new',
            component: () => import('@pages/vendor/NewDealRegForm.vue'),
            meta: {
              title: 'New Deal Registration Form',
              breadcrumb: {
                label: 'New Deal Registration Form',
              },
            },
          },
          {
            name: 'edit-deal-reg-form',
            path: 'deal-reg-forms/edit/:dealRegFormId',
            component: () => import('@pages/vendor/EditDealRegForm.vue'),
            props: route => ({ dealRegFormId: Number(route.params.dealRegFormId) }),
            meta: {
              title: 'Edit Deal Registration Form',
              breadcrumb: {
                label: 'Edit Deal Registration Form',
              },
            },
          },
          {
            name: 'new-deal-reg-field',
            path: 'deal-reg-fields/new',
            component: () => import('@pages/vendor/NewField.vue'),
            meta: {
              title: 'New Deal Registration Field',
              breadcrumb: {
                label: 'New Deal Registration Field',
              },
            },
          },
          {
            name: 'edit-deal-reg-field',
            path: 'deal-reg-fields/edit/:fieldId',
            component: () => import('@pages/vendor/EditField.vue'),
            props: route => ({ fieldId: route.params.fieldId }),
            meta: {
              title: 'Edit Deal Registration Field',
              breadcrumb: {
                label: 'Edit Deal Registration Field',
              },
            },
          },
          ...(['development', 'autoqa'].includes(import.meta.env.MODE) ?
            [
              {
                name: 'render-formkit-deal-reg-form',
                path: 'deal-reg-forms/render-formkit/:dealRegFormId',
                component: () => import('@pages/vendor/RenderFormkitDealRegForm.vue'),
                props: route => ({ dealRegFormId: Number(route.params.dealRegFormId) }),
                meta: {
                  title: 'Formkit Deal Registration Form',
                  breadcrumb: {
                    label: 'Formkit Deal Registration Form',
                  },
                },
              },
            ] :
            []),
        ]
      },
      {
        path: 'announcements',
        meta: {
          title: 'Announcements',
          breadcrumb: {
            label: 'Announcements',
            icon: 'campaign',
          },
        },
        children: [
          {
            name: 'announcements',
            path: '',
            component: () => import('@pages/vendor/announcements/VendorAnnouncementsPage.vue'),
          },
          {
            name: 'new-announcement',
            path: 'new',
            component: () => import('@pages/vendor/announcements/NewAnnouncementForm.vue'),
            meta: {
              title: 'New Announcement',
              breadcrumb: {
                label: 'New Announcement',
              },
            },
          },
          {
            name: 'edit-announcement',
            path: 'edit/:announcementId',
            component: () => import('@pages/vendor/announcements/EditAnnouncementForm.vue'),
            props: route => ({ announcementId: Number(route.params.announcementId) }),
            meta: {
              title: 'Edit Announcement',
              breadcrumb: {
                label: 'Edit Announcement',
              },
            },
          },
        ]
      },
    ]
  },
]
